/**
 * Saved-proposals table with client-side filters (the requested improvement over
 * the source app's search-only list). Built on the DS DataTable + FilterBar:
 * a free-text search plus Closer and Term multi-select chips. The proposal list
 * is fully loaded client-side, so filtering/sorting is in-memory.
 */

import { useMemo, useState } from "react";

import { Download, Link2, Trash2 } from "lucide-react";

import { DataTable, type DataTableColumn, type DataTableSort } from "@/components/cinch/DataTable/DataTable";
import { FilterBar, FilterClearAll } from "@/components/cinch/FilterBar";
import type { CheckboxOption } from "@/components/cinch/FilterBar/CheckboxList";
import { Button } from "@/components/ui/button";
import { MultiSelectChip } from "@/features/contracts/components/filters";

import { grossMrrOf, type BundleRow, type CatalogIndex, type DiscountRow, type LineRow } from "../calc";
import { buildProjection } from "../projection";
import type { Closer, Proposal } from "../api";
import { formatPrice, formatPrice2 } from "../format";
import { copyShareLink } from "../share";

interface Props {
  proposals: Proposal[];
  closers: Closer[];
  /** Catalog index — lets the table run each proposal's projection for its
   *  first-12-month value (so time-based discounts rolling off are reflected). */
  idx: CatalogIndex;
  onLoad: (p: Proposal) => void;
  /** Omitted for read-only users (proposals.read without proposals.write) —
   *  the delete action is hidden entirely when absent. */
  onDelete?: (id: string) => void;
}

/** Reconstruct the calculator's working rows from a saved proposal — server ids
 *  double as local ids, so a line's `bundle_id` maps straight to its bundle's
 *  `_id` and packages/overrides round-trip for the math helpers. */
const reconstruct = (p: Proposal): { lines: LineRow[]; bundles: BundleRow[]; discountRows: DiscountRow[] } => {
  const bundles: BundleRow[] = (p.bundles || []).map((b) => ({
    _id: b.id,
    name: b.name,
    sku: b.sku,
    descr: b.descr,
    customer_facing_descr: b.customer_facing_descr,
    pricing_type: b.pricing_type,
    price: b.price,
    price_metric: b.price_metric,
  }));
  const lines: LineRow[] = (p.line_items || []).map((li) => ({
    _id: li.id,
    bundle_local_id: li.bundle_id ?? null,
    item_id: li.item_id ?? undefined,
    package_id: li.package_id ?? undefined,
    quantity: li.quantity,
    display_as_unlimited: li.display_as_unlimited,
    unit_price: li.unit_price,
  }));
  const discountRows: DiscountRow[] = (p.discounts || []).map((d) => ({
    _id: d.id,
    discount_id: d.discount_id,
    target_line_local_id: d.target_line_item_id,
    duration_override: d.duration_override,
    unit_overrides: Object.fromEntries((d.unit_overrides || []).map((u) => [u.price_metric_id, u.quantity])),
  }));
  return { lines, bundles, discountRows };
};

/** MRR = loose lines + each bundle's (possibly overridden) price. */
const proposalMrr = (p: Proposal) => {
  const { lines, bundles } = reconstruct(p);
  return grossMrrOf(lines, bundles);
};

/** Total contract value — MRR × term (matches the builder's ARR summary card). */
const proposalArr = (p: Proposal) => proposalMrr(p) * (p.contract_months || 12);

/** First 12 months of billed value (M1–M12) from the renewal-aware projection —
 *  bundle-aware, so overridden package prices are reflected. Falls back to gross
 *  when the catalog can't resolve discounts. */
const firstTwelveMonths = (p: Proposal, idx: CatalogIndex): number => {
  const { lines, bundles, discountRows } = reconstruct(p);
  return buildProjection(
    idx,
    lines,
    discountRows,
    {
      termMonths: p.contract_months,
      isAutoRenew: p.is_auto_renew,
      renewalTerm: p.renewal_term,
      renewalTermMonths: p.renewal_term_months,
      mtmUpliftPct: p.mtm_uplift_pct,
      billingEffectiveFrom: p.billing_effective_from,
    },
    bundles,
  ).firstYearTotal; // P1 stub (if any) + M1..M12, matching Cinch's contract chart
};

export function SavedProposalsTable({ proposals, closers, idx, onLoad, onDelete }: Props) {
  const [search, setSearch] = useState("");
  const [closerIds, setCloserIds] = useState<string[]>([]);
  const [terms, setTerms] = useState<string[]>([]);
  const [sort, setSort] = useState<DataTableSort | null>({ key: "created_at", dir: "desc" });

  const closerName = (id: string | null) =>
    (id ? closers.find((c) => c.id === id)?.name : null) ?? "—";

  const closerOptions: CheckboxOption[] = useMemo(() => {
    const present = new Set(proposals.map((p) => p.closer_id).filter(Boolean) as string[]);
    const opts = closers
      .filter((c) => present.has(c.id))
      .map((c) => ({ value: c.id, label: c.name }));
    if (proposals.some((p) => !p.closer_id)) opts.push({ value: "__none__", label: "— None —" });
    return opts;
  }, [proposals, closers]);

  const termOptions: CheckboxOption[] = useMemo(() => {
    const set = new Set(proposals.map((p) => p.contract_months));
    return [...set]
      .sort((a, b) => a - b)
      .map((m) => ({ value: String(m), label: `${m} mo` }));
  }, [proposals]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let rows = proposals.filter((p) => {
      if (closerIds.length) {
        const key = p.closer_id ?? "__none__";
        if (!closerIds.includes(key)) return false;
      }
      if (terms.length && !terms.includes(String(p.contract_months))) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        (p.customer_name || "").toLowerCase().includes(q) ||
        closerName(p.closer_id).toLowerCase().includes(q)
      );
    });
    if (sort) {
      const dir = sort.dir === "asc" ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const av = sortValue(a, sort.key);
        const bv = sortValue(b, sort.key);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals, search, closerIds, terms, sort, closers]);

  function sortValue(p: Proposal, key: string): string | number {
    switch (key) {
      case "name":
        return p.name.toLowerCase();
      case "customer_name":
        return (p.customer_name || "").toLowerCase();
      case "closer":
        return closerName(p.closer_id).toLowerCase();
      case "contract_months":
        return p.contract_months;
      case "mrr":
        return proposalMrr(p);
      case "arr":
        return proposalArr(p);
      case "first12":
        return firstTwelveMonths(p, idx);
      case "created_at":
        return p.created_at;
      default:
        return "";
    }
  }

  const hasFilters = closerIds.length > 0 || terms.length > 0;

  const columns: DataTableColumn<Proposal>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (p) => <span className="font-medium">{p.name}</span>,
    },
    { key: "customer_name", header: "Customer", sortable: true, render: (p) => p.customer_name || "—" },
    { key: "closer", header: "Closer", sortable: true, render: (p) => closerName(p.closer_id) },
    {
      key: "contract_months",
      header: "Term",
      sortable: true,
      render: (p) => `${p.contract_months} mo`,
    },
    {
      key: "mrr",
      header: "MRR",
      sortable: true,
      numeric: true,
      render: (p) => formatPrice(proposalMrr(p)),
    },
    {
      key: "arr",
      header: "ARR",
      sortable: true,
      numeric: true,
      render: (p) => formatPrice(proposalArr(p)),
    },
    {
      key: "first12",
      header: "1st 12mo",
      sortable: true,
      numeric: true,
      render: (p) => formatPrice2(firstTwelveMonths(p, idx)),
    },
    {
      key: "created_at",
      header: "Saved",
      sortable: true,
      render: (p) => (
        <span className="text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (p) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => onLoad(p)}>
            <Download className="mr-1 h-3.5 w-3.5" />
            Load
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void copyShareLink(p.id)}
            aria-label={`Copy share link for ${p.name}`}
          >
            <Link2 className="h-3.5 w-3.5" />
          </Button>
          {onDelete && (
            <Button
              size="sm"
              variant="ghost"
              className="text-danger hover:text-danger"
              onClick={() => onDelete(p.id)}
              aria-label="Delete proposal"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <FilterBar>
        <MultiSelectChip label="Closer" options={closerOptions} value={closerIds} onChange={setCloserIds} />
        <MultiSelectChip label="Term" options={termOptions} value={terms} onChange={setTerms} />
        {hasFilters && (
          <FilterClearAll
            onClick={() => {
              setCloserIds([]);
              setTerms([]);
            }}
          />
        )}
      </FilterBar>
      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(p) => p.id}
        sort={sort}
        onSortChange={setSort}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search proposals…"
        emptyMessage="No saved proposals match."
        containerClassName="max-h-[32rem]"
      />
    </div>
  );
}
